import { useStaticQuery, graphql } from "gatsby";

import { useCallback, useContext, useMemo } from "react";
import { edgesToArray } from "@helpers/general";
import StoreContext from "@context/store";
import { useCurrentLang } from "./useLocalizedCartInfo";
import { useCurrentCountryCode } from "./usePrices";

function useAnnouncementBar() {
  const data = useStaticQuery(graphql`
    query {
      allContentfulAnnouncementBarContainer {
        edges {
          node {
            node_locale
            name
            countries {
              code
              country
            }
            backgroundColor {
              mobile
              desktop
            }
            mobileContent {
              ...ContentfulAnnouncementBarSectionFields
            }
            desktopContent {
              ...ContentfulAnnouncementBarSectionFields
            }
            popup {
              title
              deadlines {
                internal {
                  content
                }
              }
              backgroundImage {
                title
                url
              }
              textColor
            }
          }
        }
      }
    }
  `);

  const lang = useCurrentLang();
  const countryCode = useCurrentCountryCode();
  const { store } = useContext(StoreContext);
  const { influencerData } = store;

  let fallbackABContent = "";
  let abContent = "";

  if (data?.allContentfulAnnouncementBarContainer) {
    const allAnnouncementBar = edgesToArray(data.allContentfulAnnouncementBarContainer);

    fallbackABContent = allAnnouncementBar.find(
      (n) => n.node_locale === lang && n?.name === "FALLBACK"
    );

    const defaultAnnouncementBar = allAnnouncementBar.find(
      (n) => n?.countries?.some((c) => c.code === "RoW") && n.node_locale === lang
    );

    const localAnnouncementBar = allAnnouncementBar.find(
      (n) => n?.countries?.some((c) => c.code === countryCode) && n.node_locale === lang
    );

    abContent = localAnnouncementBar ?? defaultAnnouncementBar;
  }

  const contentAB = abContent || fallbackABContent;

  const applyInfluencerText = useCallback(
    (sections) => {
      if (!influencerData || !sections) return sections;
      return sections.map((section) => {
        if (section?.textPosition?.toLowerCase() !== "center") return section;
        return {
          ...section,
          text: section.text?.map((t) =>
            t?.isLuxeContent === "Yes"
              ? {
                  ...t,
                  text: influencerData?.influencerName
                    ? `Up to 25% Off with ${influencerData.influencerName}`
                    : `Up to 25% Off`,
                }
              : t
          ),
        };
      });
    },
    [influencerData]
  );

  const desktopContent = useMemo(
    () => applyInfluencerText(contentAB?.desktopContent),
    [contentAB?.desktopContent, applyInfluencerText]
  );

  const mobileContent = useMemo(
    () => applyInfluencerText(contentAB?.mobileContent),
    [contentAB?.mobileContent, applyInfluencerText]
  );

  const abData = useMemo(
    () => ({
      backgroundColor: contentAB?.backgroundColor,
      mobileContent,
      desktopContent,
      popup: contentAB?.popup,
      influencerData,
    }),
    [contentAB?.backgroundColor, mobileContent, desktopContent, contentAB?.popup, influencerData]
  );

  if (!data) {
    return null;
  }

  return abData;
}

export default useAnnouncementBar;
